import React, { useState, useEffect, useCallback } from 'react';
import {
    Eye, RefreshCw, ExternalLink, AlertCircle, CheckCircle, Search, FileSearch,
} from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import {
    getIndexCoverage, refreshIndexCoverage, getIndexCoverageRewrite,
    IndexCoverageResponse, IndexCoverageRow,
} from '../../services/api';
import './IndexCoveragePanel.css';

interface IndexCoveragePanelProps {
    projectId: string;
}

/** Show the URL path (more scannable than the full origin). */
const displayPage = (page: string): string => {
    try {
        const u = new URL(page);
        return (u.pathname || '/') + (u.search || '');
    } catch {
        return page;
    }
};

/**
 * Build the GSC URL Inspection deep-link client-side — mirrors
 * gsc_index_coverage.build_gsc_inspect_url(). Avoids a per-row
 * API round-trip; site_url comes from the list endpoint summary.
 */
const buildGscInspectUrl = (siteUrl: string | null, pageUrl: string): string => {
    if (!siteUrl) return 'https://search.google.com/search-console';
    return (
        'https://search.google.com/search-console/inspect' +
        '?resource_id=' + encodeURIComponent(siteUrl) +
        '&id=' + encodeURIComponent(pageUrl)
    );
};

/** Human-readable label for coverage_state_key slugs. */
const stateBadgeLabel = (key: string | null): string => {
    if (!key) return 'Unknown';
    const labels: Record<string, string> = {
        crawled_not_indexed: 'Crawled – not indexed',
        indexed: 'Indexed',
        discovered_not_indexed: 'Discovered – not indexed',
        duplicate: 'Duplicate',
        excluded_other: 'Excluded',
        blocked: 'Blocked',
        not_found: 'Not found',
        redirect: 'Redirect',
        unknown: 'Unknown',
    };
    return labels[key] ?? key;
};

interface RewriteState {
    loading: boolean;
    data: IndexCoverageRow['rewrite'] | null;
    error: string | null;
}

const IndexCoveragePanel: React.FC<IndexCoveragePanelProps> = ({ projectId }) => {
    const [data, setData] = useState<IndexCoverageResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
    // Per-row rewrite plan state: rowId → RewriteState
    const [rewriteStates, setRewriteStates] = useState<Record<number, RewriteState>>({});
    // Which rows have the plan block expanded
    const [expandedPlans, setExpandedPlans] = useState<Set<number>>(new Set());

    const fetchCoverage = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getIndexCoverage(projectId, { state: 'crawled_not_indexed' });
            setData(result);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to load index coverage data.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchCoverage();
    }, [fetchCoverage]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setRefreshMsg(null);
        try {
            const result = await refreshIndexCoverage(projectId);
            const { inspected, skipped_cached, skipped_quota, gsc_connected } = result;
            if (!gsc_connected) {
                setRefreshMsg('GSC not connected — no inspection performed.');
            } else if (inspected === 0 && skipped_quota > 0) {
                setRefreshMsg('Daily inspection quota reached. Try again tomorrow.');
            } else {
                setRefreshMsg(
                    `Inspected ${inspected} page${inspected !== 1 ? 's' : ''}` +
                    (skipped_cached > 0 ? `, ${skipped_cached} cached` : '') +
                    (skipped_quota > 0 ? `, ${skipped_quota} skipped (quota)` : '') +
                    '.'
                );
            }
            // Reload the list to show new results
            await fetchCoverage();
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setRefreshMsg(
                typeof detail === 'string' ? detail : 'Refresh failed. Try again.'
            );
        } finally {
            setRefreshing(false);
        }
    };

    const handleGetRewritePlan = async (row: IndexCoverageRow) => {
        const rowId = row.id;
        setRewriteStates(prev => ({
            ...prev,
            [rowId]: { loading: true, data: null, error: null },
        }));
        setExpandedPlans(prev => new Set(prev).add(rowId));
        try {
            const result = await getIndexCoverageRewrite(rowId);
            if (result.suggestion) {
                setRewriteStates(prev => ({
                    ...prev,
                    [rowId]: { loading: false, data: result.suggestion, error: null },
                }));
            } else {
                setRewriteStates(prev => ({
                    ...prev,
                    [rowId]: {
                        loading: false,
                        data: null,
                        error: result.error ?? 'Could not generate rewrite plan.',
                    },
                }));
            }
        } catch {
            setRewriteStates(prev => ({
                ...prev,
                [rowId]: { loading: false, data: null, error: 'Failed to generate plan.' },
            }));
        }
    };

    const togglePlan = (rowId: number) => {
        setExpandedPlans(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    };

    // ----- Render helpers -------------------------------------------------

    const renderRewritePlan = (rowId: number) => {
        const state = rewriteStates[rowId];
        if (!state) return null;
        if (!expandedPlans.has(rowId)) return null;

        if (state.loading) {
            return (
                <div className="icp-plan">
                    <RefreshCw size={14} className="icp-spinning" /> Generating rewrite plan…
                </div>
            );
        }
        if (state.error) {
            return <div className="icp-plan" style={{ color: 'var(--danger)' }}>{state.error}</div>;
        }
        if (!state.data) return null;

        const s = state.data as any;
        return (
            <div className="icp-plan">
                {s.summary && (
                    <div className="icp-plan-section">
                        <p className="icp-plan-brief">{s.summary}</p>
                    </div>
                )}
                {s.missing_originality_signals?.length > 0 && (
                    <div className="icp-plan-section">
                        <h4>Missing originality signals</h4>
                        <ul>
                            {s.missing_originality_signals.map((sig: string, i: number) => (
                                <li key={i}>{sig}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {s.data_to_add?.length > 0 && (
                    <div className="icp-plan-section">
                        <h4>Original data to add</h4>
                        <ul>
                            {s.data_to_add.map((d: string, i: number) => (
                                <li key={i}>{d}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {s.recommended_sections?.length > 0 && (
                    <div className="icp-plan-section">
                        <h4>Recommended new sections</h4>
                        <ul>
                            {s.recommended_sections.map((sec: string, i: number) => (
                                <li key={i}>{sec}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {s.rewrite_brief && (
                    <div className="icp-plan-section">
                        <h4>Rewrite brief</h4>
                        <p className="icp-plan-brief">{s.rewrite_brief}</p>
                    </div>
                )}
            </div>
        );
    };

    const renderRow = (row: IndexCoverageRow) => {
        const displayPath = displayPage(row.page_url);
        const planState = rewriteStates[row.id];
        const planExpanded = expandedPlans.has(row.id);
        const hasPlan = planState?.data != null || row.has_rewrite;

        return (
            <React.Fragment key={row.id}>
                <tr>
                    <td>
                        <div className="icp-page-cell" title={row.page_url}>
                            <a
                                className="icp-page-link"
                                href={row.page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {displayPath}
                                <ExternalLink size={11} />
                            </a>
                        </div>
                        {row.task_name && (
                            <div className="icp-task-name">{row.task_name}</div>
                        )}
                    </td>
                    <td>
                        <span className="icp-state-badge">
                            {stateBadgeLabel(row.coverage_state_key)}
                        </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        {row.last_crawl_time
                            ? new Date(row.last_crawl_time).toLocaleDateString()
                            : '—'}
                    </td>
                    <td className="icp-action-cell">
                        <div className="icp-action">
                            {row.task_id && (
                                <GlassButton
                                    variant="primary"
                                    size="sm"
                                    onClick={() =>
                                        hasPlan && planExpanded
                                            ? togglePlan(row.id)
                                            : planState?.loading
                                            ? undefined
                                            : hasPlan
                                            ? togglePlan(row.id)
                                            : handleGetRewritePlan(row)
                                    }
                                    disabled={planState?.loading}
                                    title="Get originality-focused rewrite plan from AI"
                                >
                                    {planState?.loading ? (
                                        <RefreshCw size={14} className="icp-spinning" />
                                    ) : (
                                        <FileSearch size={14} />
                                    )}
                                    {planExpanded ? 'Hide plan' : hasPlan ? 'Show plan' : 'Get rewrite plan'}
                                </GlassButton>
                            )}
                            <a
                                className="icp-gsc-link"
                                href={buildGscInspectUrl(data?.summary?.site_url ?? null, row.page_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open in GSC URL Inspection tool to request indexing"
                            >
                                <ExternalLink size={13} />
                                Inspect in GSC
                            </a>
                        </div>
                    </td>
                </tr>
                {planExpanded && (
                    <tr>
                        <td colSpan={4}>
                            {renderRewritePlan(row.id)}
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    // ----- Main render ----------------------------------------------------

    if (loading) {
        return (
            <GlassCard>
                <div className="icp-loading">
                    <RefreshCw size={16} className="icp-spinning" />
                    Loading index coverage data…
                </div>
            </GlassCard>
        );
    }

    if (error) {
        return (
            <GlassCard>
                <div className="icp-error">
                    <AlertCircle size={16} />
                    {error}
                </div>
            </GlassCard>
        );
    }

    const summary = data?.summary;
    const rows = data?.rows ?? [];
    const gscConnected = summary?.gsc_connected ?? false;

    // Not connected — prompt to connect GSC
    if (!gscConnected) {
        return (
            <GlassCard>
                <div className="icp-header">
                    <div className="icp-title">
                        <Eye size={18} />
                        Index Coverage
                    </div>
                </div>
                <div className="icp-empty">
                    <div className="icp-empty-icon">
                        <Search size={40} />
                    </div>
                    <p>Google Search Console not connected</p>
                    <p className="icp-empty-hint">
                        Connect GSC via the <strong>Performance</strong> tab to enable index coverage analysis.
                    </p>
                </div>
            </GlassCard>
        );
    }

    // Connected but no flagged pages
    if (rows.length === 0) {
        return (
            <GlassCard>
                <div className="icp-header">
                    <div className="icp-title">
                        <Eye size={18} />
                        Index Coverage
                    </div>
                    <div className="icp-header-right">
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            title="Re-inspect published pages via GSC URL Inspection API"
                        >
                            <RefreshCw size={14} className={refreshing ? 'icp-spinning' : ''} />
                            {refreshing ? 'Inspecting…' : 'Refresh'}
                        </GlassButton>
                    </div>
                </div>
                <p className="icp-subtitle">
                    Pages Google crawled but decided not to index — with originality-focused
                    rewrite plans and one-click GSC re-submission.
                </p>
                {refreshMsg && (
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                        {refreshMsg}
                    </p>
                )}
                <div className="icp-empty">
                    <div className="icp-empty-icon icp-empty-icon--ok">
                        <CheckCircle size={40} />
                    </div>
                    <p>No pages stuck in "crawled — not indexed"</p>
                    <p className="icp-empty-hint">
                        All published pages are either indexed or not yet crawled.
                        Run a refresh to inspect recently published pages.
                    </p>
                </div>
            </GlassCard>
        );
    }

    return (
        <GlassCard>
            <div className="icp-header">
                <div className="icp-title">
                    <Eye size={18} />
                    Index Coverage
                </div>
                <div className="icp-header-right">
                    {summary && (
                        <span className="icp-summary-text">
                            {summary.crawled_not_indexed} page{summary.crawled_not_indexed !== 1 ? 's' : ''} not indexed
                        </span>
                    )}
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        title="Re-inspect published pages via GSC URL Inspection API"
                    >
                        <RefreshCw size={14} className={refreshing ? 'icp-spinning' : ''} />
                        {refreshing ? 'Inspecting…' : 'Refresh'}
                    </GlassButton>
                </div>
            </div>
            <p className="icp-subtitle">
                Pages Google crawled but chose not to index. Fix originality signals and
                re-request indexing via GSC.
            </p>
            {refreshMsg && (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                    {refreshMsg}
                </p>
            )}

            <table className="seo-keyword-table icp-table">
                <thead>
                    <tr>
                        <th>Page</th>
                        <th>Coverage status</th>
                        <th>Last crawled</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(renderRow)}
                </tbody>
            </table>
        </GlassCard>
    );
};

export default IndexCoveragePanel;
